import chordFingerPositions from "@/utils/chordFingerPositions";
import { chordColors } from "@/utils/constants";
import type { Instrument } from "@/utils/types";
import cx from "classnames";
import React from "react";

const LOG_TAG = "ChordDiagram";

function ChordDiagram({
  chord,
  instrument = "guitar",
  activeChord,
}: {
  chord: string;
  instrument?: Instrument;
  activeChord?: boolean;
}) {
  const colorCode = chordColors[chord.substr(0, 1)];

  const chordsByInstrument = chordFingerPositions[instrument];
  if (!chordsByInstrument) {
    console.debug("No chords for instrument", instrument);
    return null;
  }

  const fingerPositions = chordsByInstrument[chord];
  if (!fingerPositions) {
    console.debug(LOG_TAG, "No fingerPositions for", chord, "on", instrument, {
      chordsByInstrument,
    });
    /*window.analyticsLogEvent("No Finger Position for Chord", {
      chord,
      instrument,
    });*/
    return null;
  }

  // `reverse` because we're rendering top down
  const frets = fingerPositions[0].split(" ").reverse();
  // downLevel iterator to use [...Array(5).keys()]
  const barCountArray = [0, 1, 2, 3, 4];
  return (
    <div
      className="pl-[10px] pr-[10px] py-[10px] first:pl-0"
      style={
        activeChord ? { animation: "$pulsate 2s ease-in-out infinite" } : {}
      }
    >
      <div
        className="relative flex items-center justify-center print:bg-white p-[6px] rounded-lg h-16 w-16 mb-[5px]"
        style={{ backgroundColor: `${colorCode}cc` }}
      >
        <div className="flex flex-col absolute left-1">
          {frets.map((ff, i) => (
            <div
              key={`${chord}-openBar-fret-${ff}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                i
              }`}
              className={cx("h-2 w-2", i === frets.length - 1 && "w-0 h-0")}
            >
              {ff === "0" && (
                <div
                  className={cx(
                    "border-[1px] border-[#ffffff99] w-[5px] h-[5px] mt-[-2px] rounded-full print:border-black",
                    i === 0 && "mt-[-1px]",
                    i === frets.length - 1 && "mt-[-3px]",
                  )}
                />
              )}
              {ff.toLowerCase() === "x" && (
                <div
                  className={cx(
                    i === 0 ? "mt-[-1px]" : "h-[5px] w-[5px] chord-x",
                  )}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex border-[1px] border-[#ffffffcc] rounded-l relative print:bg-black">
          {barCountArray.map((barIndex) => (
            <div key={`${chord}-bar-${barIndex}`} className="flex flex-col">
              {frets.map((ff, i) => (
                <React.Fragment
                  key={`${chord}-bar-${barIndex}-fret-${ff}-${
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    i
                  }`}
                >
                  <div
                    className={cx(
                      "border-l-[1px] border-[#00000033] relative border-t-0 h-[7px] w-2 print:border-black",
                      i === frets.length - 1 && "hidden",
                    )}
                  >
                    {ff !== "0" && Number.parseInt(ff, 10) - 1 === barIndex && (
                      <div
                        className={cx(
                          "bg-black absolute z-[2] text-center rounded-full w-[7px] h-[7px] mt-[-4px] border-[1px] print:border-white",
                          i === frets.length - 1 && "chord-last",
                        )}
                        style={{ borderColor: `${colorCode}cc` }}
                      />
                    )}
                  </div>
                  <div
                    className={cx(
                      'content-[""] h-[1px] w-2 bg-[#ffffff99] print:bg-black',
                      i >= frets.length - 2 && "hidden",
                    )}
                  />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div
        className="font-bold print:text-black"
        style={{ color: `${colorCode}` }}
      >
        {chord}
      </div>
    </div>
  );
}

export default ChordDiagram;
